import { type FC, useCallback } from 'react';

import { type MicroCopy, type Search } from '@vakantiesnl/types/src';

import { useCustomStyles } from './FlightSelector.styles';
import { getExtraPriceTitlePerPerson, getLabelText } from './helpers';
import { Radio } from '../../atoms/Radio';
import { Tag } from '../../atoms/Tag';
import { FlightInclusives } from '../FlightInclusives';
import { FlightsOverview } from '../FlightsOverview';

export type FlightSelectorProps = {
	id?: string;
	inbound: Search.Flight;
	outbound: Search.Flight;
	microCopy: MicroCopy;
	selected?: boolean;
	handleSelected?: (id: string) => void;
	extraPrice?: number;
	isLuggageInclusive: boolean;
	isTransferInclusive: boolean;
	tags?: {
		isCheapest?: boolean;
		recommended?: boolean;
	};
};

export const FlightSelector: FC<FlightSelectorProps> = (props) => {
	const { inbound, microCopy, selected, outbound, handleSelected, id, isLuggageInclusive, isTransferInclusive } =
		props;
	const { classes } = useCustomStyles();

	const onSelect = useCallback((): void => {
		if (id !== undefined && !!handleSelected) {
			handleSelected(String(id));
		}
	}, [id, handleSelected]);

	const hasInclusives = isLuggageInclusive || isTransferInclusive;

	return (
		<>
			<label
				className={classes.flightSelectorWrapper}
				data-cy={selected ? 'flightOption-selected' : `flightOption-${id}`}
			>
				<div className={classes.selectionHeader}>
					<Radio hasNoFocusState={true} name={id} id={id} checked={selected} onChange={onSelect} />
					<Tags {...props} />
				</div>

				<div className={classes.flightsContainer}>
					<FlightsOverview
						inbound={inbound}
						outbound={outbound}
						microCopy={microCopy}
						hasToggleStopOver={true}
					/>
				</div>

				{hasInclusives && (
					<div className={classes.selectionFooter}>
						<Inclusives {...props} />
					</div>
				)}
			</label>
		</>
	);
};

const Inclusives: FC<FlightSelectorProps> = ({
	microCopy,
	inbound,
	outbound,
	isLuggageInclusive,
	isTransferInclusive,
}) => {
	const { classes: styles } = useCustomStyles();

	return (
		<>
			<FlightInclusives
				containerClassName={styles.inclusives}
				inbound={inbound}
				outbound={outbound}
				isLuggageInclusive={isLuggageInclusive}
				isTransferInclusive={isTransferInclusive}
				microCopy={microCopy}
				flexDirection="row"
			/>
		</>
	);
};

const Tags: FC<FlightSelectorProps> = ({ microCopy, tags, extraPrice }) => {
	const { classes: styles } = useCustomStyles();
	const isRecommended = !!tags?.recommended;
	const isCheapest = !!tags?.isCheapest;

	return (
		<div className={styles.labels}>
			{isRecommended && (
				<Tag color="teal" size={'small'}>
					{getLabelText(false, microCopy)}
				</Tag>
			)}
			{isCheapest && (
				<Tag color="orange" size={'small'}>
					{getLabelText(true, microCopy)}
				</Tag>
			)}
			{!isCheapest && (
				<Tag variant="outline" color="orange" size={'small'}>
					{getExtraPriceTitlePerPerson(extraPrice ?? 0)}
				</Tag>
			)}
		</div>
	);
};
